/**
 * QualityDistribution Component
 *
 * Displays quality score distribution as a horizontal bar chart
 */

import React from 'react';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { QUALITY_COLORS, QUALITY_LABELS } from '../../constants';
import type { QualityDistributionData } from '../../types';

interface QualityDistributionProps {
  /** Quality distribution data */
  distribution: QualityDistributionData | undefined;
  /** Whether data is loading */
  isLoading?: boolean;
}

export const QualityDistribution: React.FC<QualityDistributionProps> = ({
  distribution,
  isLoading = false,
}) => {
  const theme = useTheme();

  // Calculate totals and percentages
  const scores = distribution
    ? [
        { score: 1, count: distribution.score_1 || 0 },
        { score: 2, count: distribution.score_2 || 0 },
        { score: 3, count: distribution.score_3 || 0 },
        { score: 4, count: distribution.score_4 || 0 },
        { score: 5, count: distribution.score_5 || 0 },
      ]
    : [];

  const total = scores.reduce((sum, s) => sum + s.count, 0);

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
        }}
      >
        <Skeleton sx={{width: "40%", height: 24,  mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i}  sx={{ height: 24 }}/>
          ))}
        </Box>
      </Paper>
    );
  }

  if (total === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Quality Distribution
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No audit data available yet
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Quality Distribution
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {scores.map(({ score, count }) => {
          const percentage = total > 0 ? (count / total) * 100 : 0;
          const color = QUALITY_COLORS[score];
          const label = QUALITY_LABELS[score];

          return (
            <Box key={score}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color,
                      minWidth: 16,
                    }}
                  >
                    {score}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {label}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {count} ({percentage.toFixed(0)}%)
                </Typography>
              </Box>
              <Box
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: theme.palette.grey[100],
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${percentage}%`,
                    bgcolor: color,
                    borderRadius: 1,
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default QualityDistribution;

